import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function KeyRow({ st, save }) {
  const { t } = useT();
  const [key, setKey] = useState("");
  const test = useMutation({
    mutationFn: () => api.post("/ai-email/test-key"),
    onSuccess: (r) => toast.success(`✓ ${r.data.model} ${t("answered")}: ${r.data.reply}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Key test failed"),
  });
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="ai-key-row">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs font-semibold">{t("OpenAI API key")}</Label>
        {st.openai_key_set ? (
          <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-300 font-mono text-[11px]" data-testid="ai-key-set">{st.openai_key_hint}</Badge>
        ) : st.using_emergent_fallback ? (
          <Badge className="bg-amber-50 text-amber-800 border border-amber-300 text-[11px]" data-testid="ai-key-fallback">{t("Using the built-in Emergent key until you add yours")}</Badge>
        ) : (
          <Badge className="bg-red-50 text-red-800 border border-red-300 text-[11px]" data-testid="ai-key-missing">{t("Missing")}</Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk-…" className="max-w-sm font-mono" data-testid="ai-key-input" />
        <Button onClick={() => save.mutate({ openai_api_key: key }, { onSuccess: () => setKey("") })} disabled={!key.trim() || save.isPending} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ai-key-save">{t("Save key")}</Button>
        <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !(st.openai_key_set || st.using_emergent_fallback)} data-testid="ai-key-test">{test.isPending ? t("Testing…") : `🔌 ${t("Test")}`}</Button>
        {st.openai_key_set && <Button variant="outline" onClick={() => save.mutate({ clear_key: true })} className="text-red-700 border-red-200" data-testid="ai-key-clear">{t("Remove")}</Button>}
      </div>
      <p className="text-[11px] text-slate-500">{t("Create a key at platform.openai.com → API keys. Billed to your OpenAI account.")}</p>
    </div>
  );
}

function GmailRow({ st }) {
  const { t } = useT();
  const { user } = useAuth();
  const connect = useMutation({
    mutationFn: async () => (await api.get("/gmail/connect")).data,
    onSuccess: (d) => { const w = window.open(d?.authorization_url, "_blank", "noopener"); if (!w) window.location.href = d?.authorization_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to start Gmail OAuth"),
  });
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex flex-wrap items-center gap-2" data-testid="ai-gmail-row">
      <Label className="text-xs font-semibold">{t("Gmail inbox")}</Label>
      {st.gmail_inbox_access ? (
        <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-300 text-[11px]" data-testid="ai-gmail-ok">✓ {t("Inbox access granted")} · {st.gmail_email}</Badge>
      ) : (
        <Badge className="bg-red-50 text-red-800 border border-red-300 text-[11px]" data-testid="ai-gmail-missing">{st.gmail_connected ? t("Connected for sending only — re-connect to read the inbox") : t("Gmail not connected")}</Badge>
      )}
      {!st.gmail_inbox_access && (user.role === "super_admin" ? (
        <Button size="sm" onClick={() => connect.mutate()} disabled={connect.isPending} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ai-gmail-connect">🔐 {t("Grant inbox access")}</Button>
      ) : <span className="text-[11px] text-slate-500">{t("Ask the Super Admin to re-connect Gmail")}</span>)}
    </div>
  );
}

export function AiEmailCard({ expanded = false }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { data: st } = useQuery({ queryKey: ["ai-email-settings"], queryFn: async () => (await api.get("/ai-email/settings")).data });
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(() => expanded || (typeof window !== "undefined" && window.location.hash === "#ai-email"));
  useEffect(() => { if (st && !form) setForm({ signature: st.signature, timeline_note: st.timeline_note, poll_minutes: st.poll_minutes, min_confidence: st.min_confidence }); }, [st, form]);
  const save = useMutation({
    mutationFn: (patch) => api.put("/ai-email/settings", patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ai-email-settings"] }); toast.success(t("Saved")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  if (!st || !form) return null;
  const toggleTopic = (k) => save.mutate({ allowed_topics: st.allowed_topics.includes(k) ? st.allowed_topics.filter((x) => x !== k) : [...st.allowed_topics, k] });
  if (!open && !st.enabled) {
    return (
      <div id="ai-email" className="bg-white border border-dashed border-slate-300 rounded-lg px-6 py-4 flex flex-wrap items-center gap-3" data-testid="ai-email-card-collapsed">
        <span className="text-xl">🤖</span>
        <div className="flex-1 min-w-[240px]">
          <div className="font-semibold text-slate-700">{t("Optional: CRM-side Gmail assistant")} <Badge className="ml-1 bg-slate-100 text-slate-600 border border-slate-300">{t("OFF")}</Badge></div>
          <div className="text-xs text-slate-500">{t("Not needed when your ChatGPT reads Gmail itself (see ChatGPT Connector above). Lets the CRM read the inbox and answer with its own OpenAI key.")}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="ai-email-expand">{t("Show settings")}</Button>
      </div>
    );
  }
  return (
    <div id="ai-email" className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="ai-email-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl">🤖</span>
        <h2 className="font-semibold text-lg">{t("AI E-mail Assistant (ChatGPT)")}</h2>
        <Badge className={`border ${st.enabled ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid="ai-enabled-badge">{st.enabled ? t("ON") : t("OFF")}</Badge>
        {!st.enabled && <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="ai-email-collapse">{t("Hide")}</Button>}
        <div className="ml-auto flex items-center gap-2 text-sm">
          <Label htmlFor="ai-enabled" className="text-xs">{t("Read inbox & answer")}</Label>
          <Switch id="ai-enabled" checked={!!st.enabled} onCheckedChange={(v) => save.mutate({ enabled: v })} data-testid="ai-enabled-switch" />
        </div>
      </div>
      <p className="text-sm text-slate-600">{t("ChatGPT reads new customer e-mails in the connected Gmail inbox, looks the sender up in the CRM and answers from the order data. Simple questions go out automatically; anything else waits in the")} <Link to="/ai-inbox" className="underline font-semibold">{t("AI Inbox")}</Link>.</p>
      <KeyRow st={st} save={save} />
      <GmailRow st={st} />
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-md border border-slate-200 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs font-semibold">{t("Auto-send simple answers")}</Label>
            <Switch checked={!!st.auto_send} onCheckedChange={(v) => save.mutate({ auto_send: v })} data-testid="ai-autosend-switch" />
          </div>
          <p className="text-[11px] text-slate-500">{t("Only when the sender matches a customer, the topic is allowed and ChatGPT is confident. Off = every reply waits for review.")}</p>
          <Label className="text-xs">{t("Model")}</Label>
          <select value={st.model} onChange={(e) => save.mutate({ model: e.target.value })} className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="ai-model-select">
            {st.models.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{t("Check every (min)")}</Label><Input type="number" min={2} max={60} value={form.poll_minutes} onChange={(e) => setForm({ ...form, poll_minutes: e.target.value })} onBlur={() => save.mutate({ poll_minutes: Number(form.poll_minutes) })} data-testid="ai-poll-minutes" /></div>
            <div><Label className="text-xs">{t("Min. confidence")}</Label><Input type="number" min={0.5} max={1} step={0.05} value={form.min_confidence} onChange={(e) => setForm({ ...form, min_confidence: e.target.value })} onBlur={() => save.mutate({ min_confidence: Number(form.min_confidence) })} data-testid="ai-min-confidence" /></div>
          </div>
        </div>
        <div className="rounded-md border border-slate-200 p-3 space-y-1.5" data-testid="ai-topics">
          <Label className="text-xs font-semibold">{t("ChatGPT may answer on its own about")}</Label>
          {Object.entries(st.topics).map(([k, l]) => (
            <label key={k} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`ai-topic-${k}`}>
              <input type="checkbox" checked={st.allowed_topics.includes(k)} onChange={() => toggleTopic(k)} className="mt-1" data-testid={`ai-topic-check-${k}`} />
              <span>{t(l)}{k === "money" && <span className="text-[11px] text-slate-400"> · {t("usually safer with a human")}</span>}</span>
            </label>
          ))}
          <p className="text-[11px] text-slate-500">{t("Anything else is drafted but held for your review.")}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">{t("Timeline ChatGPT may quote")}</Label><Textarea rows={3} value={form.timeline_note} onChange={(e) => setForm({ ...form, timeline_note: e.target.value })} onBlur={() => save.mutate({ timeline_note: form.timeline_note })} className="text-sm" data-testid="ai-timeline-note" /></div>
        <div><Label className="text-xs">{t("Signature")}</Label><Textarea rows={3} value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} onBlur={() => save.mutate({ signature: form.signature })} className="text-sm" data-testid="ai-signature" /></div>
      </div>
      {st.last_poll && (
        <div className="text-[11px] font-mono text-slate-500" data-testid="ai-last-poll">
          {t("last check")} {String(st.last_poll_at || "").slice(0, 16).replace("T", " ")} · {st.last_poll.error ? `⚠ ${st.last_poll.error}` : `${st.last_poll.new} ${t("new")} · ${st.last_poll.auto_sent} ${t("auto-sent")} · ${st.last_poll.held} ${t("held")}`}
        </div>
      )}
    </div>
  );
}
